import { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AppleAuthButton } from "@/components/ui/auth/apple-auth-button";
import { GoogleAuthButton } from "@/components/ui/auth/google-auth-button";
import { EmailAuthButton } from "@/components/ui/auth/email-auth-button";
import { RegisterForm } from "@/components/forms/RegisterForm";
import { LegalLinks } from "@/components/ui/legal-links";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { useMobile } from "@/providers/mobileProvider";
import { PATHS } from "@/config/paths";
import { registerDialog } from "@/state/dialogs";

type RegisterDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onboarding?: boolean;
  goBack?: () => void;
};

export const RegisterDialog = ({
  open,
  onOpenChange,
  onboarding = false,
  goBack,
}: RegisterDialogProps) => {
  const router = useRouter();
  const { isMobile, isAndroid } = useMobile();
  const [showEmailForm, setShowEmailForm] = useState(false);
  const setRegisterDialogOpen = useSetAtom(registerDialog);
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) {
      setShowEmailForm(false);
    }
  }, [open]);

  const continueWithEmail = () => {
    if (isMobile) {
      router.navigate({ to: PATHS.REGISTER });
      setRegisterDialogOpen(false);
    } else {
      setShowEmailForm(true);
    }
  };

  const cardContent = (
    <>
      <DialogHeader className="w-full">
        <AnimatePresence mode="wait">
          {!showEmailForm && (
            <motion.div
              initial={{ x: 0, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-5%", opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="flex flex-col gap-2"
            >
              <div className="text-lg font-semibold leading-none tracking-tight flex items-center gap-2">
                {onboarding && goBack && (
                  <Button variant="ghost" size="icon" className="size-8" onClick={() => goBack?.()}>
                    <ArrowLeft />
                  </Button>
                )}
                {t("auth.register.title")}
              </div>

              <div className="text-sm text-muted-foreground">{t("auth.register.description")}</div>
            </motion.div>
          )}

          {showEmailForm && (
            <motion.div
              key="email-header"
              initial={{ x: "5%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "5%", opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <DialogTitle className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={() => setShowEmailForm(false)}
                >
                  <ArrowLeft />
                </Button>
                {t("auth.register.continue_with_email")}
              </DialogTitle>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogHeader>

      <div className="grid gap-6 relative">
        <AnimatePresence mode="wait">
          {!showEmailForm && (
            <motion.div
              key="auth-buttons"
              initial={{ x: 0, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-5%", opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="flex flex-col gap-3"
            >
              {!isAndroid && <AppleAuthButton isRegister />}
              <GoogleAuthButton isRegister />
              <EmailAuthButton isRegister onClick={continueWithEmail} />
            </motion.div>
          )}

          {showEmailForm && (
            <motion.div
              key="email-form"
              initial={{ x: "5%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "5%", opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
            >
              <RegisterForm />
            </motion.div>
          )}
        </AnimatePresence>

        <LegalLinks closeDialog={() => onOpenChange(false)} />
      </div>
    </>
  );

  if (onboarding) {
    return cardContent;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>{cardContent}</DialogContent>
    </Dialog>
  );
};
